<template>
  <div class="notion-wiki-container">
    <v-progress-linear
      v-if="loadPending"
      indeterminate
      absolute
      top
      color="green"
    ></v-progress-linear>

    <div v-if="showSidebar" class="sidebar">
      <v-treeview :items="sidebarItems" :open="sidebarOpen" multipleActive transition>
        <template slot="label" slot-scope="props">
          <router-link :to="mapPageUrl(props.item.id)" class="sidebar-link">
            {{ props.item.name }}
          </router-link>
        </template>
      </v-treeview>
    </div>
    <div v-else class="sidebar">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div class="content">
      <NotionRenderer
        :blockMap="currentBlockMap"
        :mapPageUrl="mapPageUrl"
        :mapImageUrl="mapImageUrl"
        :pageLinkOptions="$options.pageLinkOptions"
        :customBlockComponents="customBlocks"
        fullPage
        prism
        katex
      />
    </div>
  </div>
</template>

<script>
import { getPageBlocks } from "@/plugins/notion";
import { NotionRenderer } from "vue-notion";
import { mapActions, mapGetters } from "vuex";
import { TableBlock } from "./wiki/TableBlock.vue";

const clearUUIDash = (id) => id.replace(/-/g, "");

const addUUIDash = (id) =>
  `${id.substr(0, 8)}-${id.substr(8, 4)}-${id.substr(12, 4)}-${id.substr(16, 4)}-${id.substr(20)}`;

const ensureUUIDash = (id) => addUUIDash(clearUUIDash(id));

export default {
  name: "NotionWiki",
  components: { NotionRenderer },
  props: {
    homeId: String,
    rootId: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
    name: String,
  },
  data: () => ({
    currentBlockMap: null,
    customBlocks: { table: TableBlock },
    sidebarItems: [],

    sidebarOpen: [],
    unofficialLoadPending: false,
  }),
  computed: {
    ...mapGetters({
      wikiLoadPending: "wiki/loadPending",
      getImage: "wiki/getImage",
    }),
    showSidebar() {
      return this.rootId && this.sidebarItems.length > 0;
    },
    blockId() {
      const id = this.$route.params.id ?? this.homeId;
      return id;
    },
    sidebarActive() {
      return this.sidebarParents[ensureUUIDash(this.blockId)] ?? [];
    },
    loadPending() {
      return this.unofficialLoadPending || this.wikiLoadPending;
    },
  },
  methods: {
    ...mapActions({
      wikiGet: "wiki/get",
      getSidebar: "wiki/getSidebar",
      loadImage: "wiki/getImage",
      // wikiFind: 'wiki/find' /* getWikiImage: "wiki/getImage" */
    }),
    mapPageUrl(pageId = "") {
      return `/${this.name}/${pageId.replace(/-/g, "")}`;
    },
    mapImageUrl(imageUrl, block) {
      const { id } = block.value;
      const img = this.getImage(id);
      return img;
    },
    preloadImages(blockMap) {
      if (!blockMap) return;

      // Extract all image blocks and preload them
      Object.values(blockMap).forEach((block) => {
        if (block.value && block.value.type === "image") {
          this.loadImage(block.value.id);
        }
      });
    },
    getPageBlocks(...args) {
      this.unofficialLoadPending = true;

      return getPageBlocks(...args).then((blocks) => {
        this.unofficialLoadPending = false;

        const formated = Object.fromEntries(
          Object.entries(blocks).map(([key, entry]) => [
            key,
            entry?.value?.value ? { ...entry, value: entry.value.value } : entry,
          ])
        );

        return formated;
      });
    },
    async nav(target = null) {
      const t = target || this.blockId;
      this.currentBlockMap = await this.getPageBlocks(t);
    },
  },
  watch: {
    "$route.params.id": function (to) {
      this.nav(to);
    },
    currentBlockMap: {
      handler(newBlockMap) {
        // Pre-load images when block map changes
        this.preloadImages(newBlockMap);
      },
      immediate: true,
    },
  },
  async mounted() {
    this.$options.pageLinkOptions = {
      component: "router-link",
      href: "to",
    };
    if (this.rootId) {
      this.getSidebar(ensureUUIDash(this.rootId))
        .then((sidebar) => {
          if (sidebar?.items) {
            this.sidebarItems = sidebar.items;
            if (sidebar.parents) {
              this.sidebarOpen = sidebar.parents[ensureUUIDash(this.blockId)] || [];
            }
          }
        })
        .catch((error) => {
          console.error("Error loading sidebar:", error);
          // Keep the loading state visible
        });
    }

    this.nav(this.blockId);
  },
};
</script>

<style>
@import "../assets/styles/notion.css";
/* optional Notion-like styles */

/* Override Vuetify treeview styles for better text wrapping */
.v-treeview-node__label {
  word-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  line-height: 1.4 !important;
  max-width: 100% !important;
}

@media (min-width: 769px) {
  .notion-wiki-container {
    display: flex;
    height: 100%;
    position: relative;
  }

  /* space looks weird when sidebar is same color as content */
  .sidebar {
    width: 320px;
    border-right: 1px solid #e0e0e0;
    padding: 16px;
    background-color: #fafafa;
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
  }

  .content {
    flex: 1;
    padding: 16px;
  }

  /* use more of the page */
  .notion-page {
    max-width: 1008px;
  }

  /* but don't let images get too tall */
  .notion-asset-wrapper {
    max-width: 600px;
  }

  .notion-asset-wrapper > img {
    max-width: 600px;
  }
}
</style>
